import { Typography } from '@mui/material'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'
import type { PublicTicket } from '../../ticketService'
import { TicketQrCode } from '../TicketQrCode/TicketQrCode'
import { nativeTicketDate, ticketPhrasesFor } from '../../ticketLanguage'
import styles from './TicketCard.module.scss'

const atNoon = (value: string) => new Date(`${value}T12:00:00`)

/** "Tuesday 1 December 2026" (long) or "Tue 1 Dec 2026" (short), built from parts so every browser shows it the same way. */
function formatDate(value: string, style: 'long' | 'short') {
  const parts = new Intl.DateTimeFormat('en-GB', { weekday: style, day: 'numeric', month: style, year: 'numeric' }).formatToParts(atNoon(value))
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? ''
  return `${part('weekday')} ${part('day')} ${part('month')} ${part('year')}`
}

/** One day in full; several days as a short range so it still fits on a phone. */
export function ticketDate(ticket: Pick<PublicTicket, 'eventDate' | 'endDate'>) {
  if (!ticket.endDate || ticket.endDate === ticket.eventDate) return formatDate(ticket.eventDate, 'long')
  return `${formatDate(ticket.eventDate, 'short')} – ${formatDate(ticket.endDate, 'short')}`
}

/** The start and end times, and the event's time zone so a visitor elsewhere isn't misled. */
export function ticketTime(ticket: Pick<PublicTicket, 'startTime' | 'endTime' | 'timezone'>) {
  const start = ticket.startTime?.slice(0, 5) ?? null
  const end = ticket.endTime?.slice(0, 5) ?? null
  const times = start && end ? `${start}–${end}` : start ?? end
  return times ? { times, timezone: ticket.timezone } : null
}

/**
 * The ticket as plain text, for pasting into a message: who it's for, the
 * event, when and where, and the link that opens the ticket and its QR code.
 * Like the ticket itself, it carries no contact details.
 */
export function ticketDetailsText(ticket: PublicTicket, ticketUrl: string) {
  const time = ticketTime(ticket)
  return [
    `${ticket.attendeeName}, your ticket for ${ticket.eventName}`,
    `🗓️ ${ticketDate(ticket)}`,
    ...(time ? [`🕒 ${time.times} (${time.timezone})`] : []),
    `📍 ${ticket.location}`,
    `🎟️ #${ticket.reference}: ${ticketUrl}`,
  ].join('\n')
}

/**
 * The ticket as the attendee sees it, styled as an entry ticket: a gold band
 * with the reference, the event and when and where, a tear line, then the
 * guest and the QR code the door scans. Shared by the public ticket page and
 * View ticket in All Mine, so both always show the same ticket.
 *
 * Deliberately not on it: the attendee's contact details (anyone holding the
 * link can see the ticket). The QR code stays large and dark on white so it
 * scans in poor light, and is drawn on the device from the ticket link.
 */
/**
 * `fitScreen` lets the QR code take whatever height is left, so the whole
 * ticket fits in View ticket without scrolling. It never drops below a size
 * that scans easily: when even that doesn't fit (a long name or venue on a
 * small phone), the card stops shrinking and the dialog scrolls instead,
 * rather than the card cutting the code off under its footer.
 */
function useCrowded(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null)
  const [crowded, setCrowded] = useState(false)
  useLayoutEffect(() => {
    if (!enabled) return
    const body = ref.current
    if (!body || typeof ResizeObserver === 'undefined') return
    // Measured with the card fitted (not crowded): does the body's content
    // spill past the body, into the footer or beyond the card? Checked again
    // whenever the ticket changes size: the screen turns, fonts arrive, the
    // code is drawn.
    let frame = 0
    const measure = () => {
      cancelAnimationFrame(frame)
      setCrowded(false)
      frame = requestAnimationFrame(() => setCrowded(body.scrollHeight > body.clientHeight + 1))
    }
    measure()
    const article = body.parentElement
    const observer = new ResizeObserver(() => { if (!body.closest(`.${styles.crowded}`)) measure() })
    observer.observe(body)
    for (const child of Array.from(body.children)) observer.observe(child)
    if (article) observer.observe(article)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [enabled])
  return [ref, crowded] as const
}

export function TicketCard({ ticket, ticketUrl, titleId, heading = 'h1', fitScreen = false }: Readonly<{ ticket: PublicTicket; ticketUrl: string; titleId?: string; heading?: 'h1' | 'h2'; fitScreen?: boolean }>) {
  const [cardRef, crowded] = useCrowded(fitScreen)
  const time = ticketTime(ticket)
  // The local language of the event's place, under the English (none for English-speaking places).
  const native = ticketPhrasesFor(ticket.timezone)
  const inNative = (text: string) => (native ? <span lang={native.lang} className={styles.native}>{text}</span> : null)
  // Each row is marked with an emoji; the word stays for screen readers.
  const details: ReadonlyArray<readonly [string, string, ReactNode]> = [
    ['Date', '🗓️', <>{ticketDate(ticket)}{native ? inNative(nativeTicketDate(native.lang, ticket.eventDate, ticket.endDate)) : null}</>],
    ...(time ? [['Time', '🕒', <>{time.times} <span className={styles.zone}>{time.timezone}</span></>] as const] : []),
    ['Venue', '📍', ticket.location],
  ]
  return (
    <article className={fitScreen ? `${styles.ticket} ${styles.fit}${crowded ? ` ${styles.crowded}` : ''}` : styles.ticket} aria-labelledby={titleId}>
      <header className={styles.band}>
        <span className={styles.kindGroup}><span className={styles.kind}>Free entry ticket</span>{native ? <span lang={native.lang} className={styles.kindNative}>{native.freeEntry}</span> : null}</span>
        <span className={styles.reference}>#{ticket.reference}</span>
      </header>
      <div ref={cardRef} className={styles.body}>
        <Typography component={heading} id={titleId} className={styles.event}>{ticket.eventName}</Typography>
        <dl className={styles.details}>
          {details.map(([label, icon, value]) => (
            <div key={label} className={styles.detail}>
              <dt><span aria-hidden="true">{icon}</span><span className={styles.visuallyHidden}>{label}</span></dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <div className={styles.tear} role="presentation" />
        <div className={styles.guest}>
          <span className={styles.guestLabel}>Guest{native ? <span lang={native.lang}> · {native.guest}</span> : null}</span>
          <span className={styles.guestName}>{ticket.attendeeName}</span>
          {ticket.invitedBy ? <span className={styles.invitedBy}>Invited by {ticket.invitedBy}{native ? <span lang={native.lang} className={styles.native}>{native.invitedBy(ticket.invitedBy)}</span> : null}</span> : null}
        </div>
        <div className={styles.qr}><TicketQrCode value={ticketUrl} label={`${ticket.attendeeName}’s ticket`} /></div>
      </div>
      <footer className={styles.footer}>Show this code at the door for entry{inNative(native?.showCode ?? '')}</footer>
    </article>
  )
}
